/**
 * View helpers for the staff billing page (TRA-1163). Pure functions over
 * the generated response types, so the mode copy is pinned by tests. Money
 * and rate formatting come from the customer page's helpers, so a rate
 * reads the same to staff and to the team.
 */

import type { ConfiguredModelView, TeamBillingAdminResponseMode, UnpricedModelView } from '@/api/generated/model'
import { spendLabel as windowSpendLabel } from '@/components/billing/billing'

export {
  LIST_PRICE_BADGE,
  listPriceSourceLabel,
  pricingLabel,
  rateLabel,
} from '@/components/billing/billing'

export const MODE_LABELS: Record<TeamBillingAdminResponseMode, string> = {
  unconfigured: 'No billing config',
  blacklisted: 'Blacklisted',
  display_only: 'Display only',
  billable: 'Billable',
}

export const MODE_DESCRIPTIONS: Record<TeamBillingAdminResponseMode, string> = {
  unconfigured:
    'This team has no billing block. Nothing is priced, and the nightly cycle alerts on its usage.',
  blacklisted: 'This team is never billed or alerted on.',
  display_only: 'Rates price the dashboard only. The billing cycle skips this team.',
  billable: 'The nightly cycle meters this team into Stripe.',
}

/**
 * What the priced-models table says about itself. A model is priced by
 * its own entry in the billing block or by the catalog list price of the
 * slug it calls (TRA-1282), and hourly spend needs its caveat.
 */
export function pricedModelsDescription(rows: ConfiguredModelView[]): string {
  const base =
    'Each model has a rate of its own or a catalog list price. A list price applies only to requests a catalog provider served. Requests and spend cover the last 30 days.'
  return rows.some((row) => row.pricing.kind === 'hourly')
    ? `${base} Hourly spend is what the GPU invoice charges for those days. It is split across the requests that ran on the GPU.`
    : base
}

/**
 * The window's spend for a priced model; unpriced traffic is never a $0.
 * When only part of the traffic had a rate (a catalog model whose fallback
 * ran elsewhere), the figure says how much of it it covers.
 */
export function spendLabel(row: ConfiguredModelView): string {
  const spend = windowSpendLabel(row.window_request_count, row.window_cost_usd)
  if (row.window_cost_usd === null || row.window_priced_request_count >= row.window_request_count) return spend
  return `${spend} for ${row.window_priced_request_count.toLocaleString('en-US')} of ${row.window_request_count.toLocaleString('en-US')} requests`
}

/**
 * The unpriced requests on a model with traffic without a rate. A partly
 * priced model (a catalog model whose fallback ran on a private upstream)
 * says how much of its traffic that is.
 */
export function unpricedRequestsLabel(row: UnpricedModelView): string {
  const unpriced = row.window_unpriced_request_count.toLocaleString('en-US')
  return row.window_unpriced_request_count < row.window_request_count
    ? `${unpriced} of ${row.window_request_count.toLocaleString('en-US')}`
    : unpriced
}

/** An empty model key is traffic the gateway never attributed to a model. */
export function modelLabel(model: string): string {
  return model === '' ? 'unattributed requests' : model
}
